/**
 * Home-screen widget data bridge.
 *
 * The app computes a small JSON snapshot (core widgetSnapshot: today's drawing prompt, sparks, level)
 * and hands it to the native widgets:
 *   Android  @capacitor/preferences writes SharedPreferences "CapacitorStorage"; the AppWidgetProvider
 *            reads key WIDGET_SNAPSHOT_KEY from there. The optional WidgetBridge plugin also asks the
 *            widget to refresh right away.
 *   iOS      WidgetKit reads an App Group (WIDGET_APP_GROUP) that Preferences cannot write, so the small
 *            WidgetBridge plugin (apps/client/native-widgets/ios/WidgetBridgePlugin.swift) stores the JSON
 *            there and reloads the timelines.
 *   Web      PWA manifest shortcuts; /today opens the same daily prompt.
 * Widget taps open sketchling://open/<path> (or https://…/today), routed by App.tsx.
 */
import { App as CapApp } from '@capacitor/app';
import { registerPlugin } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { profileAccess, WIDGET_SNAPSHOT_KEY, widgetSnapshot, type WidgetSnapshot } from '@sketchling/core';
import { CONTENT, IX } from './content';
import { isNative } from './platform';
import { appNow, EMPTY_PROGRESS, selectTier, useApp } from '../state/store';

interface WidgetBridgePlugin {
  setSnapshot(o: { key: string; json: string }): Promise<void>;
}
const WidgetBridge = registerPlugin<WidgetBridgePlugin>('WidgetBridge');


export function currentSnapshot(): WidgetSnapshot {
  const s = useApp.getState();
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) ?? null;
  const progress = (profile && s.progress[profile.id]) || null;
  const access = profile ? profileAccess(selectTier(s), s.profiles.map((p) => p.id), profile.id, s.entitlement.premiumProfileId) : 'limited';
  return widgetSnapshot({
    now: appNow(),
    lang: s.settings.language,
    profileId: profile?.id ?? null,
    profileName: profile?.name ?? null,
    progress: progress ?? EMPTY_PROGRESS,
    playground: CONTENT.playground,
    drawings: IX.drawings,
    premium: access === 'full' || !!s.account.unlockAll || (import.meta.env.DEV && s.cheats.forcePremium),
  });
}

let lastJson = '';
export async function publishWidget(): Promise<void> {
  const json = JSON.stringify({ ...currentSnapshot(), updatedAt: undefined });
  if (json === lastJson) return;
  lastJson = json;
  const full = JSON.stringify(currentSnapshot());
  if (!isNative) {
    try {
      localStorage.setItem(WIDGET_SNAPSHOT_KEY, full);
    } catch {
      /* ignore */
    }
    return;
  }
  await Preferences.set({ key: WIDGET_SNAPSHOT_KEY, value: full }).catch(() => undefined);
  await WidgetBridge.setSnapshot({ key: WIDGET_SNAPSHOT_KEY, json: full }).catch(() => undefined);
}

/** Map a widget / deep-link URL to an in-app path. */
export function pathFromAppUrl(url: string): string | null {
  // Parsed by hand: older Android WebViews (Chrome < 130) give non-special URLs an empty host and a "//open/…"
  // pathname, which became a protocol-relative "//open/…" route and opened https://open/… in the browser.
  const custom = /^sketchling:\/\/(?:open)?(\/[^?#]*)?(\?[^#]*)?/i.exec(url);
  if (custom) {
    const path = (custom[1] ?? '/').replace(/^\/+/, '/');
    return path + (custom[2] ?? '');
  }
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/^\/+/, '/');
    return path.startsWith('/') ? path + u.search : null;
  } catch {
    return null;
  }
}

let started = false;
export function initWidget(navigate: (path: string) => void): void {
  if (started) return;
  started = true;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const schedule = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void publishWidget(), 2000);
  };
  useApp.subscribe((st, prev) => {
    if (st.progress !== prev.progress || st.activeProfileId !== prev.activeProfileId || st.settings.language !== prev.settings.language || st.entitlement !== prev.entitlement) schedule();
  });
  schedule();
  if (isNative) {
    void CapApp.addListener('appUrlOpen', ({ url }) => {
      const p = pathFromAppUrl(url);
      if (p) navigate(p);
    });
    void CapApp.addListener('resume', schedule);
  }
}
